/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { OpenSidebar } from './tab.OpenSidebar';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { TabSortable } from './tab.Sortable';
import {
  canEditItemForItem,
  getId,
  isBuiltinTabId,
  isTabCampaignAccessible,
} from '../../utils/item';
import { update } from '../../services/item';
import { TabNew } from './tab.New';
import { useWindowSize } from '../../hooks/useWindowSize';
import { setNavigationOpen } from '../../store/global';
import { FaEllipsisV, FaSearch, FaTimes } from 'react-icons/fa';
import { TabInList } from './tab.InList';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';

export const TabAll = props => {
  const {
    itemId,
    containerWidth,
    ignoreSidebar,
    useMobileTabState,
    hideNavigation,
    sidebarOpenOverride,
    onSidebarOpen,
  } = props;

  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const isModalOpen = useSelector(state => state.global.openModales);
  const sidebar = useSelector(state => state.global.sidebar);
  const navigation = useSelector(state => state.global.navigation);
  const currentTab = useSelector(state => state.global.currentTab);
  const mobileCurrentTab = useSelector(
    state => state.global.currentMobileTab[itemId]
  );
  const listCampaignsId = useSelector(state => state.global.listCampaignsId);
  const dispatch = useDispatch();

  const [tabs, setTabs] = useState([]);
  const [disable, setDisable] = useState(false);
  const [openAllTabs, setOpenAllTabs] = useState(false);

  const windowSize = useWindowSize();
  const visibleTabs = tabs.filter(tabObj => {
    const tabId = getId(tabObj);
    const tab = items?.[tabId];
    if (!tab) {
      return isBuiltinTabId(tabId);
    }
    return isTabCampaignAccessible(tab, items, listCampaignsId);
  });
  const activeTabId = useMobileTabState ? mobileCurrentTab : currentTab;
  const canEdit = canEditItemForItem(item, items);
  const canBypassReadOnly = isMjForItem(item, items) || canUseMjForItem(item, items);
  const canReorderTabs = canEdit && (!item?.readOnlyMode || canBypassReadOnly);

  const tabsRef = useRef(tabs);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = event => {
    if (!canReorderTabs) {
      return;
    }
    const { active, over } = event;
    if (!over?.id || active?.id === over.id) {
      return;
    }
    if (active.id !== over.id) {
      setTabs(prev => {
        const oldIndex = prev.findIndex(
          item => Object.keys(item)[0] === active.id
        );
        const newIndex = prev.findIndex(
          item => Object.keys(item)[0] === over.id
        );
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  useEffect(() => {
    if (item?.tabs) {
      setTabs(item?.tabs);
    }
  }, [item?.tabs]);

  useEffect(() => {
    if (
      tabs?.length === item?.tabs?.length &&
      JSON.stringify(tabs) !== JSON.stringify(item?.tabs)
    ) {
      if (canReorderTabs) {
        update(`items/${itemId}`, 'tabs', tabs);
      }
    }
  }, [tabs, canReorderTabs]);

  useEffect(() => {
    let isDisable = false;
    for (const tab of tabs) {
      for (const id of Object.keys(isModalOpen)) {
        if (getId(tab) === id) {
          if (isModalOpen[id] === true) {
            isDisable = true;
            setDisable(true);
          }
        }
      }
    }
    if (!isDisable) {
      setDisable(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    setOpenAllTabs(false);
  }, [activeTabId]);

  return (
    <div className='flex bg-main shadow-sm items-center gap-1 border-b border-main-d border-opacity-20'>
      <OpenSidebar
        itemId={itemId}
        sidebarOpenOverride={sidebarOpenOverride}
        onSidebarOpen={onSidebarOpen}
      />
      <div
        ref={ref => {
          tabsRef.current = ref;
        }}
        className='flex items-center flex-1 overflow-x-auto overflow-y-hidden'
        style={{
          width:
            (typeof containerWidth === 'number'
              ? containerWidth
              : windowSize.width) -
            (ignoreSidebar ? 0 : sidebar.size) -
            100,
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={canReorderTabs ? handleDragEnd : undefined}
          modifiers={[restrictToHorizontalAxis]}
        >
          <SortableContext
            items={visibleTabs.map(tabObj => getId(tabObj))}
            strategy={horizontalListSortingStrategy}
            disabled={disable || !canReorderTabs}
          >
            {visibleTabs.map(tabObj => (
              <TabSortable
                key={getId(tabObj)}
                id={getId(tabObj)}
                tabId={getId(tabObj)}
                itemId={itemId}
                useMobileTabState={useMobileTabState}
                mobileCurrentTab={mobileCurrentTab}
                draggable={canReorderTabs}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <div className='relative'>
        <div
          className='transition hover:opacity-50 cursor-pointer'
          onClick={() => setOpenAllTabs(!openAllTabs)}
        >
          <FaEllipsisV />
        </div>
        {openAllTabs && (
          <div className='absolute bg-main shadow rounded z-10 right-0'>
            {visibleTabs.map(tabObj => (
              <TabInList
                key={getId(tabObj)}
                tabId={getId(tabObj)}
                itemId={itemId}
                useMobileTabState={useMobileTabState}
                mobileCurrentTab={mobileCurrentTab}
              />
            ))}
          </div>
        )}
      </div>

      {canEdit && <TabNew itemId={itemId} />}
      {!hideNavigation && (
        <>
          <div
            onClick={() => dispatch(setNavigationOpen(!navigation.open))}
            className={`transition cursor-pointer hover:opacity-50 border-primary border-left ${
              navigation.open ? '' : 'mr-1'
            }`}
          >
            {navigation.open ? <FaTimes /> : <FaSearch />}
          </div>
          <div
            style={{ width: navigation.open ? navigation.size : 0 }}
          ></div>
        </>
      )}
    </div>
  );
};
